import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { categoryAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Tag,
  Scissors,
  Package,
  FolderOpen
} from 'lucide-react';

// Form component outside main component
function CategoryForm({ formData, setFormData }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Categoria *</Label>
        <Input
          placeholder="es. Tagli, Trattamenti, Styling..."
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Textarea
          placeholder="Descrizione della categoria..."
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>Tipo</Label>
        <Select
          value={formData.type}
          onValueChange={(v) => setFormData(prev => ({ ...prev, type: v }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="service">
              <div className="flex items-center gap-2">
                <Scissors className="w-4 h-4" />
                Servizi
              </div>
            </SelectItem>
            <SelectItem value="product">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Prodotti
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Ordine di visualizzazione</Label>
        <Input
          type="number"
          min="0"
          placeholder="0"
          value={formData.sort_order}
          onChange={(e) => setFormData(prev => ({ ...prev, sort_order: e.target.value }))}
        />
        <p className="text-xs text-muted-foreground">Numeri più bassi appaiono prima</p>
      </div>
    </div>
  );
}

export default function CategoryManager({ categories, services, products, onRefresh }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('service');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'service',
    sort_order: '0'
  });

  const serviceCategories = categories.filter(c => c.type === 'service');
  const productCategories = categories.filter(c => c.type === 'product');

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: activeTab,
      sort_order: '0'
    });
  };

  const openCreate = () => {
    resetForm();
    setFormData(prev => ({ ...prev, type: activeTab }));
    setIsCreateOpen(true);
  };

  const openEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      type: category.type || 'service',
      sort_order: category.sort_order?.toString() || '0'
    });
    setIsEditOpen(true);
  };

  const openDelete = (category) => {
    setSelectedCategory(category);
    setIsDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('Il nome è obbligatorio');
      return;
    }

    setLoading(true);
    try {
      await categoryAPI.create({
        name: formData.name,
        description: formData.description || null,
        type: formData.type,
        sort_order: parseInt(formData.sort_order) || 0
      });
      toast.success('Categoria creata con successo');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCategory) return;

    setLoading(true);
    try {
      await categoryAPI.update(selectedCategory.id, {
        name: formData.name,
        description: formData.description || null,
        type: formData.type,
        sort_order: parseInt(formData.sort_order) || 0
      });
      toast.success('Categoria aggiornata');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    setLoading(true);
    try {
      await categoryAPI.delete(selectedCategory.id);
      toast.success('Categoria eliminata');
      setIsDeleteOpen(false);
      setSelectedCategory(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  const getItemCount = (category) => {
    if (category.type === 'service') {
      return services.filter(s => s.category_id === category.id).length;
    } else {
      return products.filter(p => p.category_id === category.id).length;
    }
  };

  const renderCategoryCard = (category) => (
    <Card key={category.id} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              category.type === 'service' ? 'bg-primary/10' : 'bg-blue-100'
            }`}>
              {category.type === 'service' ? (
                <Scissors className="w-5 h-5 text-primary" />
              ) : (
                <Package className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <p className="font-semibold">{category.name}</p>
              <Badge variant="outline" className="text-xs">
                {getItemCount(category)} {category.type === 'service' ? 'servizi' : 'prodotti'}
              </Badge>
            </div>
          </div>
        </div>
        
        {category.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{category.description}</p>
        )}
        
        <div className="flex items-center justify-between pt-3 border-t">
          <span className="text-xs text-muted-foreground">Ordine: {category.sort_order || 0}</span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEdit(category)}
              data-testid={`edit-category-${category.id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDelete(category)}
              className="text-destructive hover:text-destructive"
              data-testid={`delete-category-${category.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Gestione Categorie</CardTitle>
          <CardDescription>Organizza servizi e prodotti in categorie</CardDescription>
        </div>
        <Button onClick={openCreate} data-testid="create-category-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Categoria
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="service" className="flex items-center gap-2">
              <Scissors className="w-4 h-4" />
              Servizi ({serviceCategories.length})
            </TabsTrigger>
            <TabsTrigger value="product" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Prodotti ({productCategories.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="service">
            {serviceCategories.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Nessuna categoria servizi</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea la prima categoria
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {serviceCategories.map(cat => renderCategoryCard(cat))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="product">
            {productCategories.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Nessuna categoria prodotti</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea la prima categoria
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {productCategories.map(cat => renderCategoryCard(cat))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Nuova Categoria
            </DialogTitle>
            <DialogDescription>
              Crea una nuova categoria per organizzare servizi o prodotti
            </DialogDescription>
          </DialogHeader>
          <CategoryForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creazione...' : 'Crea Categoria'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifica Categoria
            </DialogTitle>
          </DialogHeader>
          <CategoryForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription asChild>
              <div>
                Sei sicuro di voler eliminare questa categoria?
                {selectedCategory && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <p><strong>Nome:</strong> {selectedCategory.name}</p>
                    <p><strong>Tipo:</strong> {selectedCategory.type === 'service' ? 'Servizi' : 'Prodotti'}</p>
                    <p><strong>Elementi:</strong> {getItemCount(selectedCategory)}</p>
                  </div>
                )}
                {selectedCategory && getItemCount(selectedCategory) > 0 && (
                  <p className="mt-2 text-amber-600 text-sm">
                    Attenzione: gli elementi in questa categoria rimarranno senza categoria.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
